import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Image, Type, Save, Share2, Download, Sparkles, Heart, Target, Palette, Trash2, Upload, Edit3, ArrowLeft, Eye, Maximize2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useAuth } from '../AuthContext';
import { useToast } from '../hooks/use-toast';
import html2canvas from 'html2canvas';

const VisionBoard = () => {
  const { user, token } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('gallery'); // gallery, create, edit
  const [creationMode, setCreationMode] = useState('manual'); // manual, ai_assisted
  const [visionBoards, setVisionBoards] = useState([]);
  const [currentBoard, setCurrentBoard] = useState(null);
  const [boardElements, setBoardElements] = useState([]);
  const [selectedElement, setSelectedElement] = useState(null);
  const [showImageLibrary, setShowImageLibrary] = useState(false);
  const [manifestationPrompts, setManifestationPrompts] = useState({});
  const [aiPromptResponses, setAiPromptResponses] = useState({});
  const [draggedElement, setDraggedElement] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isEditingText, setIsEditingText] = useState(false);
  const [unsplashImages, setUnsplashImages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showFullScreen, setShowFullScreen] = useState(false);
  const [fullScreenBoard, setFullScreenBoard] = useState(null);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [curatedStockImages, setCuratedStockImages] = useState({});
  const canvasRef = useRef(null);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    if (user && token) {
      loadVisionBoards();
      loadCuratedStockImages();
    }
  }, [user, token]);

  const loadCuratedStockImages = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/curated-stock-images`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCuratedStockImages(data.images);
        console.log('✅ Loaded curated stock images:', Object.keys(data.images).length, 'categories');
      }
    } catch (error) {
      console.error('Error loading curated stock images:', error);
    }
  };

  const loadVisionBoards = async () => {
    console.log('📚 Loading vision boards...');
    try {
      const response = await fetch(`${API_BASE_URL}/api/vision-boards`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Load boards response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Boards loaded:', data);
        console.log('Number of boards:', data.boards?.length || 0);
        setVisionBoards(data.boards || []);
      } else {
        console.error('Failed to load boards:', response.status);
      }
    } catch (error) {
      console.error('❌ Error loading vision boards:', error);
    }
  };

  const createNewBoard = (mode) => {
    setCreationMode(mode);
    setViewMode('create');
    setBoardElements([]);
    setCurrentBoard({
      title: mode === 'ai_assisted' ? `Vision Board - ${new Date().toLocaleDateString()}` : '',
      description: mode === 'ai_assisted' ? 'AI-generated manifestation board' : '',
      category: 'general',
      background_color: '#FEF7ED'
    });
  };

  const addImageElement = (imageUrl) => {
    const newElement = {
      id: Date.now().toString(),
      element_type: 'image',
      content: imageUrl,
      position_x: Math.random() * 60 + 10, // Random position 10-70%
      position_y: Math.random() * 60 + 10,
      width: 25,
      height: 20,
      z_index: boardElements.length + 1
    };
    
    setBoardElements([...boardElements, newElement]);
    setShowImageLibrary(false);
  };

  const addTextElement = () => {
    const newElement = {
      id: Date.now().toString(),
      element_type: 'text',
      content: 'Your affirmation here',
      position_x: Math.random() * 50 + 10,
      position_y: Math.random() * 70 + 10,
      width: 40,
      height: 8,
      z_index: boardElements.length + 1,
      style: {
        fontSize: '24px',
        fontWeight: 'bold',
        color: '#f43f5e',
        fontFamily: 'serif'
      }
    };
    
    setBoardElements([...boardElements, newElement]);
  };

  const deleteElement = (elementId) => {
    setBoardElements(boardElements.filter(el => el.id !== elementId));
    setSelectedElement(null);
  };

  const updateElementContent = (elementId, newContent) => {
    setBoardElements(boardElements.map(el => 
      el.id === elementId ? { ...el, content: newContent } : el
    ));
  };

  const handleMouseDown = (e, element) => {
    if (isEditingText) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const offsetX = e.clientX - rect.left - (rect.width * element.position_x / 100);
    const offsetY = e.clientY - rect.top - (rect.height * element.position_y / 100);
    
    setDraggedElement(element);
    setDragOffset({ x: offsetX, y: offsetY });
    setSelectedElement(element);
  };

  const handleMouseMove = (e) => {
    if (!draggedElement || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    const x = ((e.clientX - rect.left - dragOffset.x) / rect.width) * 100;
    const y = ((e.clientY - rect.top - dragOffset.y) / rect.height) * 100;
    
    // Constrain to canvas bounds
    const constrainedX = Math.max(0, Math.min(100 - draggedElement.width, x));
    const constrainedY = Math.max(0, Math.min(100 - draggedElement.height, y));
    
    setBoardElements(boardElements.map(el => 
      el.id === draggedElement.id 
        ? { ...el, position_x: constrainedX, position_y: constrainedY }
        : el
    ));
  };

  const handleMouseUp = () => {
    setDraggedElement(null);
    setIsResizing(false);
    setResizeHandle(null);
  };

  const handleResizeStart = (e, element, handle) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeHandle(handle);
    setSelectedElement(element);
    setDraggedElement(element);
  };

  const handleResizeMove = (e) => {
    if (!isResizing || !draggedElement || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    const mouseX = ((e.clientX - rect.left) / rect.width) * 100;
    const mouseY = ((e.clientY - rect.top) / rect.height) * 100;
    
    setBoardElements(boardElements.map(el => {
      if (el.id !== draggedElement.id) return el;
      
      let newWidth = el.width;
      let newHeight = el.height;
      let newX = el.position_x;
      let newY = el.position_y;
      
      // Calculate new dimensions based on resize handle
      if (resizeHandle === 'se') {
        newWidth = Math.max(10, mouseX - el.position_x);
        newHeight = Math.max(10, mouseY - el.position_y);
      } else if (resizeHandle === 'sw') {
        const rightEdge = el.position_x + el.width;
        newX = Math.min(mouseX, rightEdge - 10);
        newWidth = rightEdge - newX;
        newHeight = Math.max(10, mouseY - el.position_y);
      } else if (resizeHandle === 'ne') {
        newWidth = Math.max(10, mouseX - el.position_x);
        const bottomEdge = el.position_y + el.height;
        newY = Math.min(mouseY, bottomEdge - 10);
        newHeight = bottomEdge - newY;
      } else if (resizeHandle === 'nw') {
        const rightEdge = el.position_x + el.width;
        const bottomEdge = el.position_y + el.height;
        newX = Math.min(mouseX, rightEdge - 10);
        newY = Math.min(mouseY, bottomEdge - 10);
        newWidth = rightEdge - newX;
        newHeight = bottomEdge - newY;
      }
      
      // Constrain to canvas bounds
      newWidth = Math.min(newWidth, 100 - newX);
      newHeight = Math.min(newHeight, 100 - newY);
      
      return {
        ...el,
        width: newWidth,
        height: newHeight,
        position_x: newX,
        position_y: newY
      };
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid File",
        description: "Please upload an image file",
        variant: "destructive",
      });
      return;
    }
    
    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Please upload an image smaller than 5MB",
        variant: "destructive",
      });
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_BASE_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });
      
      if (response.ok) {
        const data = await response.json();
        addImageElement(data.url);
        toast({
          title: "Image Uploaded! 🎨",
          description: "Your image has been added to the board",
        });
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      toast({
        title: "Upload Failed",
        description: "Unable to upload image. Please try again.",
        variant: "destructive",
      });
    }
  };

  const searchUnsplashImages = async (query) => {
    try {
      // Search within curated stock images first
      if (Object.keys(curatedStockImages).length > 0) {
        const lowerQuery = query.toLowerCase();
        const searchResults = [];
        
        // Search through all categories
        Object.entries(curatedStockImages).forEach(([category, images]) => {
          // Check if category matches query
          if (category.toLowerCase().includes(lowerQuery)) {
            searchResults.push(...images);
          }
        });
        
        // Also check specific keywords
        const keywordMap = {
          'car': 'lifestyle',
          'porsche': 'lifestyle',
          'office': 'career',
          'work': 'career',
          'yoga': 'wellness',
          'meditation': 'wellness',
          'couple': 'relationships',
          'love': 'relationships',
          'beach': 'travel',
          'travel': 'travel',
          'fashion': 'fashion',
          'nature': 'nature',
          'luxury': 'luxury'
        };
        
        Object.entries(keywordMap).forEach(([keyword, category]) => {
          if (lowerQuery.includes(keyword) && curatedStockImages[category]) {
            searchResults.push(...curatedStockImages[category]);
          }
        });
        
        // Remove duplicates
        const uniqueResults = [...new Set(searchResults)];
        setUnsplashImages(uniqueResults.slice(0, 30)); // Limit to 30 results
        
        if (uniqueResults.length === 0) {
          // If no matches, show all images from a relevant category
          const allImages = Object.values(curatedStockImages).flat();
          setUnsplashImages(allImages.slice(0, 30));
        }
        
        console.log(`✅ Found ${uniqueResults.length} images for "${query}"`);
        return;
      }
      
      // Fallback to Unsplash API if curated images not loaded
      const response = await fetch(`${API_BASE_URL}/api/unsplash/search?query=${encodeURIComponent(query)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setUnsplashImages(data.images || []);
      }
    } catch (error) {
      console.error('Error searching images:', error);
      toast({
        title: "Search Error",
        description: "Unable to search images. Please try browsing categories instead.",
        variant: "destructive",
      });
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      searchUnsplashImages(searchQuery);
    }
  };

  const saveVisionBoard = async () => {
    console.log('💾 Starting vision board save...');
    console.log('Current board:', currentBoard);
    console.log('Board elements:', boardElements);
    
    if (!currentBoard?.title?.trim()) {
      console.error('❌ No title provided');
      toast({
        title: "Title Required",
        description: "Please add a title to your vision board",
        variant: "destructive",
      });
      return;
    }

    try {
      const boardData = {
        ...currentBoard,
        elements: boardElements,
        creation_mode: creationMode
      };
      
      console.log('Board data to save:', boardData);

      const method = currentBoard.id ? 'PUT' : 'POST';
      const url = currentBoard.id 
        ? `${API_BASE_URL}/api/vision-boards/${currentBoard.id}`
        : `${API_BASE_URL}/api/vision-boards`;

      console.log(`📡 ${method} ${url}`);
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(boardData),
      });

      console.log('Save response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Board saved:', result);
        
        toast({
          title: currentBoard.id ? "✨ Vision Board Updated!" : "✨ Vision Board Created!",
          description: result.first_board ? "You've earned the Vision Creator Badge! 🎨" : "Your manifestation board is ready",
          duration: 5000,
        });
        
        console.log('🔄 Reloading boards...');
        // Reload boards first, then switch view
        await loadVisionBoards();
        
        console.log('📋 Switching to gallery...');
        setViewMode('gallery');
        // Clear current editing state
        setCurrentBoard(null);
        setBoardElements([]);
        
        console.log('✅ Save complete!');
      } else {
        const errorData = await response.json().catch(() => ({detail: 'Unknown error'}));
        console.error('Save failed:', errorData);
        throw new Error(errorData.detail || 'Save failed');
      }
    } catch (error) {
      console.error('❌ Vision board save error:', error);
      toast({
        title: "Save Failed",
        description: error.message || "Unable to save your vision board",
        variant: "destructive",
      });
    }
  };

  const editBoard = async (board) => {
    console.log('✏️ Editing board:', board);
    try {
      const response = await fetch(`${API_BASE_URL}/api/vision-boards/${board.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const boardData = await response.json();
        console.log('📦 Board data loaded:', boardData);
        setCurrentBoard(boardData);
        setBoardElements(boardData.elements || []);
        setCreationMode('manual'); // Always use manual mode for editing
        setViewMode('create');
        
        // Show helpful message
        const isAIGenerated = boardData.creation_mode === 'ai_assisted';
        toast({
          title: isAIGenerated ? "✨ AI Board Loaded - Now Customize It!" : "Board Loaded! ✏️",
          description: isAIGenerated 
            ? "Drag images around, resize, delete, or add new ones to make it perfect for you!" 
            : "You can now edit your vision board",
        });
      } else {
        throw new Error('Failed to load board');
      }
    } catch (error) {
      console.error('❌ Edit board error:', error);
      toast({
        title: "Load Failed",
        description: "Unable to load vision board",
        variant: "destructive",
      });
    }
  };

  const deleteBoard = async (boardId) => {
    if (!window.confirm('Are you sure you want to delete this vision board?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/vision-boards/${boardId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast({
          title: "Board Deleted",
          description: "Vision board has been removed",
        });
        loadVisionBoards();
      }
    } catch (error) {
      toast({
        title: "Delete Failed",
        description: "Unable to delete vision board",
        variant: "destructive",
      });
    }
  };

  const viewBoardFullScreen = (board) => {
    console.log('👁️ Opening full-screen view for board:', board);
    setFullScreenBoard(board);
    setShowFullScreen(true);
  };

  const downloadBoard = async (board) => {
    console.log('💾 Downloading board:', board.title);
    
    // Create a temporary div to render the board
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.width = '1200px';
    tempDiv.style.height = '675px';
    tempDiv.style.backgroundColor = board.background_color || '#FEF7ED';
    document.body.appendChild(tempDiv);

    // Render elements
    board.elements?.forEach(element => {
      const elemDiv = document.createElement('div');
      elemDiv.style.position = 'absolute';
      elemDiv.style.left = `${element.position_x}%`;
      elemDiv.style.top = `${element.position_y}%`;
      elemDiv.style.width = `${element.width}%`;
      elemDiv.style.height = `${element.height}%`;
      
      if (element.element_type === 'image') {
        const img = document.createElement('img');
        img.src = element.content;
        img.style.width = '100%';
        img.style.height = '100%';
        img.style.objectFit = 'cover';
        img.style.borderRadius = '8px';
        elemDiv.appendChild(img);
      } else {
        elemDiv.innerHTML = element.content;
        elemDiv.style.display = 'flex';
        elemDiv.style.alignItems = 'center';
        elemDiv.style.justifyContent = 'center';
        elemDiv.style.textAlign = 'center';
        elemDiv.style.padding = '10px';
        elemDiv.style.borderRadius = '8px';
        elemDiv.style.backgroundColor = 'rgba(255, 255, 255, 0.8)';
        if (element.style) {
          Object.assign(elemDiv.style, element.style);
        }
      }
      
      tempDiv.appendChild(elemDiv);
    });

    try {
      // Wait a bit for images to load
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const canvas = await html2canvas(tempDiv, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: board.background_color || '#FEF7ED'
      });
      
      const link = document.createElement('a');
      link.download = `${board.title || 'vision-board'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast({
        title: "Download Complete! 💾",
        description: "Your vision board has been saved",
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Download Failed",
        description: "Unable to download vision board",
        variant: "destructive",
      });
    } finally {
      document.body.removeChild(tempDiv);
    }
  };

  const generateAIBoard = async () => {
    console.log('🎨 Starting AI board generation...');
    console.log('Current board:', currentBoard);
    console.log('AI prompt responses:', aiPromptResponses);
    
    // Show beautiful loading screen
    setIsGenerating(true);
    
    try {
      console.log('📡 Calling /api/vision-boards/generate...');
      const response = await fetch(`${API_BASE_URL}/api/vision-boards/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          category: currentBoard.category,
          prompts: aiPromptResponses
        }),
      });

      console.log('Generate response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Generate failed:', errorText);
        throw new Error(`Generation failed: ${response.status}`);
      }

      const aiBoard = await response.json();
      console.log('✅ AI board generated:', aiBoard);
      
      // Check if AI generation returned elements
      if (!aiBoard.elements || aiBoard.elements.length === 0) {
        console.error('No elements in AI board');
        toast({
          title: "⚠️ Generation Issue",
          description: "Unable to generate AI elements. Please try again or use Manual Creation mode.",
          variant: "destructive",
          duration: 6000,
        });
        return;
      }
      
      console.log(`📦 Saving board with ${aiBoard.elements.length} elements...`);
      
      // Save the generated board
      const boardToSave = {
        title: currentBoard.title || `Vision Board - ${new Date().toLocaleDateString()}`,
        description: currentBoard.description || "AI-generated vision board",
        category: currentBoard.category,
        background_color: currentBoard.background_color || '#FEF7ED',
        elements: aiBoard.elements
      };
      
      console.log('Board to save:', boardToSave);
      
      const saveResponse = await fetch(`${API_BASE_URL}/api/vision-boards`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(boardToSave),
      });
      
      console.log('Save response status:', saveResponse.status);
      
      if (!saveResponse.ok) {
        const errorText = await saveResponse.text();
        console.error('Save failed:', errorText);
        throw new Error(`Save failed: ${saveResponse.status}`);
      }
      
      const savedBoard = await saveResponse.json();
      console.log('✅ Board saved successfully:', savedBoard);
      
      toast({
        title: "✨ Vision Board Created!",
        description: `Your board has been saved with ${aiBoard.elements.length} inspiring elements`,
        duration: 4000,
      });
      
      console.log('🔄 Reloading vision boards...');
      // Reload boards and go to gallery
      await loadVisionBoards();
      
      console.log('📋 Switching to gallery view...');
      setViewMode('gallery');
      setCurrentBoard(null);
      setBoardElements([]);
      setAiPromptResponses({});
      
      console.log('✅ Generation complete!');
    } catch (error) {
      console.error('❌ Vision board generation error:', error);
      toast({
        title: "Generation Failed",
        description: error.message || "Unable to generate AI vision board. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Curated stock images for vision boards - organized by theme
  const stockImages = {
    "Success & Career": [
      "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=300",
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300",
      "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=400&h=300",
      "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=300",
      "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=400&h=300",
      "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&h=300"
    ],
    "Love & Relationships": [
      "https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=400&h=300",
      "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=400&h=300",
      "https://images.unsplash.com/photo-1518199266791-5375a83190b7?w=400&h=300",
      "https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=400&h=300",
      "https://images.unsplash.com/photo-1511988617509-a57c8a288659?w=400&h=300",
      "https://images.unsplash.com/photo-1541534741688-6078c6bfb5c5?w=400&h=300"
    ],
    "Home & Lifestyle": [
      "https://images.unsplash.com/photo-1484154218962-a197022b5858?w=400&h=300",
      "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=400&h=300",
      "https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=400&h=300",
      "https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?w=400&h=300",
      "https://images.unsplash.com/photo-1556912173-46c336c7fd55?w=400&h=300",
      "https://images.unsplash.com/photo-1600210492493-0946911123ea?w=400&h=300"
    ],
    "Wellness & Self-Care": [
      "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=300",
      "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=400&h=300",
      "https://images.unsplash.com/photo-1515377905703-c4788e51af15?w=400&h=300",
      "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300",
      "https://images.unsplash.com/photo-1600334129128-685c5582fd35?w=400&h=300",
      "https://images.unsplash.com/photo-1593811167562-9cef47bfc4d7?w=400&h=300"
    ],
    "Travel & Adventure": [
      "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=400&h=300",
      "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=400&h=300",
      "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=400&h=300",
      "https://images.unsplash.com/photo-1539635278303-d4002c07eae3?w=400&h=300",
      "https://images.unsplash.com/photo-1503220317375-aaad61436b1b?w=400&h=300",
      "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=400&h=300"
    ],
    "Fashion & Beauty": [
      "https://images.unsplash.com/photo-1483985988355-763728e1935b?w=400&h=300",
      "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=400&h=300",
      "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=400&h=300",
      "https://images.unsplash.com/photo-1445205170230-053b83016050?w=400&h=300",
      "https://images.unsplash.com/photo-1512496015851-a90fb38ba796?w=400&h=300",
      "https://images.unsplash.com/photo-1485968579580-b6d095142e6e?w=400&h=300"
    ],
    "Nature & Serenity": [
      "https://images.unsplash.com/photo-1502082553048-f009c37129b9?w=400&h=300",
      "https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=400&h=300",
      "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&h=300",
      "https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=400&h=300",
      "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300",
      "https://images.unsplash.com/photo-1475924156734-496f6cac6ec1?w=400&h=300"
    ],
    "Inspiration & Motivation": [
      "https://images.unsplash.com/photo-1494537176433-7a3c4ef2046f?w=400&h=300",
      "https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=400&h=300",
      "https://images.unsplash.com/photo-1517960413843-0aee8e2b3285?w=400&h=300",
      "https://images.unsplash.com/photo-1495837174058-628aafc7d610?w=400&h=300",
      "https://images.unsplash.com/photo-1488998527040-85054a85150e?w=400&h=300",
      "https://images.unsplash.com/photo-1513829596324-4bb2800c5efb?w=400&h=300"
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-rose-100 pb-24" style={{paddingTop: '64px'}}>
      <div className="max-w-7xl mx-auto px-4">
        
        {/* Header */}
        <div className="flex items-center space-x-2 mb-4">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 hover:bg-white/80 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-serif font-bold text-gray-900">Vision Board</h1>
            <p className="text-gray-600 text-xs">Visualize your dreams</p>
          </div>
        </div>

        {/* Gallery View */}
        {viewMode === 'gallery' && (
          <div className="space-y-8">
            
            {/* Create New Board Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card 
                className="p-8 bg-gradient-to-br from-rose-50 to-pink-50 border-2 border-rose-200 cursor-pointer hover:shadow-xl transition-all"
                onClick={() => createNewBoard('manual')}
              >
                <div className="text-center">
                  <div className="text-6xl mb-4">🎨</div>
                  <h3 className="text-xl font-serif font-bold text-gray-900 mb-2">Manual Editor</h3>
                  <p className="text-gray-600 font-sans mb-4">
                    Upload your own photos, arrange images, and add personal text to create your perfect vision board
                  </p>
                  <div className="bg-white/80 rounded-lg p-3">
                    <p className="text-rose-800 text-sm font-sans">
                      ✨ Drag & drop images • Add affirmations • Full creative control
                    </p>
                  </div>
                </div>
              </Card>

              <Card 
                className="p-8 bg-gradient-to-br from-purple-50 to-indigo-50 border-2 border-purple-200 cursor-pointer hover:shadow-xl transition-all"
                onClick={() => createNewBoard('ai_assisted')}
              >
                <div className="text-center">
                  <div className="text-6xl mb-4">✨</div>
                  <h3 className="text-xl font-serif font-bold text-gray-900 mb-2">AI-Assisted Creation</h3>
                  <p className="text-gray-600 font-sans mb-4">
                    Answer guided prompts about your dreams and let AI create a beautiful vision board for you
                  </p>
                  <div className="bg-white/80 rounded-lg p-3">
                    <p className="text-purple-800 text-sm font-sans">
                      🤖 AI-generated images • Personalized layout • Instant creation
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Existing Vision Boards */}
            {visionBoards.length > 0 && (
              <div>
                <h2 className="text-2xl font-serif font-bold text-gray-900 mb-6">Your Vision Boards</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {visionBoards.map((board) => (
                    <Card key={board.id} className="p-4 bg-white border-rose-200 hover:shadow-lg transition-shadow">
                      <div 
                        className="aspect-video bg-gradient-to-br from-rose-50 to-pink-50 rounded-lg mb-3 flex items-center justify-center cursor-pointer overflow-hidden relative"
                        style={{ backgroundColor: board.background_color }}
                        onClick={() => editBoard(board)}
                      >
                        {board.elements && board.elements.length > 0 ? (
                          <div className="absolute inset-0">
                            {board.elements.slice(0, 3).map((element, idx) => (
                              <div
                                key={idx}
                                className="absolute"
                                style={{
                                  left: `${element.position_x}%`,
                                  top: `${element.position_y}%`,
                                  width: `${element.width}%`,
                                  height: `${element.height}%`,
                                }}
                              >
                                {element.element_type === 'image' ? (
                                  <img 
                                    src={element.content} 
                                    alt=""
                                    className="w-full h-full object-cover rounded opacity-80"
                                  />
                                ) : (
                                  <div 
                                    className="text-xs text-center truncate"
                                    style={element.style}
                                  >
                                    {element.content}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-4xl">🖼️</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-serif font-semibold text-gray-900">{board.title}</h3>
                        {board.creation_mode === 'ai_assisted' && (
                          <span className="text-xs bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 px-2 py-0.5 rounded-full font-sans">
                            ✨ AI
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 text-sm font-sans line-clamp-2">
                        {board.creation_mode === 'ai_assisted' 
                          ? 'AI-generated board - Click Edit to customize!' 
                          : (board.description || 'No description')}
                      </p>
                      <div className="mt-3 space-y-2">
                        <div className="flex gap-2">
                          <Button 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              viewBoardFullScreen(board);
                            }}
                            className="flex-1 bg-purple-500 hover:bg-purple-600 text-white rounded-full font-sans text-xs"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            View
                          </Button>
                          <Button 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadBoard(board);
                            }}
                            className="flex-1 bg-green-500 hover:bg-green-600 text-white rounded-full font-sans text-xs"
                          >
                            <Download className="w-3 h-3 mr-1" />
                            Download
                          </Button>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              editBoard(board);
                            }}
                            className="flex-1 bg-blue-500 hover:bg-blue-600 text-white rounded-full font-sans text-xs"
                          >
                            <Edit3 className="w-3 h-3 mr-1" />
                            Edit
                          </Button>
                          <Button 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteBoard(board.id);
                            }}
                            className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-full font-sans text-xs"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {visionBoards.length === 0 && (
              <Card className="p-12 bg-white/80 backdrop-blur-sm border-2 border-rose-200 text-center">
                <div className="text-6xl mb-6">🌟</div>
                <h3 className="text-2xl font-serif font-bold text-gray-900 mb-4">
                  Create Your First Vision Board
                </h3>
                <p className="text-gray-600 font-sans mb-8 max-w-2xl mx-auto">
                  Vision boards are powerful manifestation tools that help you visualize your dreams and goals. 
                  Start creating the life you desire through the power of visualization!
                </p>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                  <p className="text-yellow-800 text-sm font-sans">
                    💡 <strong>NeuroLove Method™:</strong> Visualization rewires your brain to recognize opportunities and take aligned action toward your dreams.
                  </p>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Creation Mode - Manual Editor */}
        {viewMode === 'create' && creationMode === 'manual' && (
          <div className="space-y-6">
            
            {/* Board Setup */}
            <Card className="p-6 bg-white/90 backdrop-blur-sm border-2 border-rose-200">
              <h2 className="text-2xl font-serif font-bold text-gray-900 mb-6">Create Your Vision Board</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 font-sans">Board Title</label>
                  <input
                    type="text"
                    value={currentBoard?.title || ''}
                    onChange={(e) => setCurrentBoard({...currentBoard, title: e.target.value})}
                    placeholder="e.g., My 2025 Dreams, Career Goals, Love & Relationships"
                    className="w-full px-3 py-2 border border-rose-200 rounded-lg focus:ring-rose-500 focus:border-rose-500 font-sans"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 font-sans">Category</label>
                  <select
                    value={currentBoard?.category || 'general'}
                    onChange={(e) => setCurrentBoard({...currentBoard, category: e.target.value})}
                    className="w-full px-3 py-2 border border-rose-200 rounded-lg focus:ring-rose-500 focus:border-rose-500 font-sans"
                  >
                    <option value="general">General Dreams</option>
                    <option value={`${new Date().getFullYear()}_goals`}>{new Date().getFullYear()} Goals</option>
                    <option value="career">Career & Purpose</option>
                    <option value="love_relationships">Love & Relationships</option>
                    <option value="health">Health & Wellness</option>
                    <option value="lifestyle">Dream Lifestyle</option>
                  </select>
                </div>
              </div>

              {/* Tools */}
              <div className="flex flex-wrap gap-3 mb-6">
                <Button 
                  onClick={() => setShowImageLibrary(true)}
                  className="bg-blue-500 hover:bg-blue-600 text-white rounded-full font-sans"
                >
                  <Image className="w-4 h-4 mr-2" />
                  Browse Images
                </Button>
                
                <label className="cursor-pointer">
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <Button 
                    type="button"
                    className="bg-purple-500 hover:bg-purple-600 text-white rounded-full font-sans pointer-events-none"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Image
                  </Button>
                </label>
                
                <Button 
                  onClick={addTextElement}
                  className="bg-green-500 hover:bg-green-600 text-white rounded-full font-sans"
                >
                  <Type className="w-4 h-4 mr-2" />
                  Add Text
                </Button>
                
                <Button 
                  onClick={saveVisionBoard}
                  disabled={!currentBoard?.title?.trim()}
                  className="bg-rose-500 hover:bg-rose-600 text-white rounded-full font-sans disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {currentBoard?.id ? 'Update Board' : 'Save Board'}
                </Button>
                
                <Button 
                  onClick={() => {
                    setViewMode('gallery');
                    setCurrentBoard(null);
                    setBoardElements([]);
                  }}
                  variant="outline"
                  className="border-rose-300 text-rose-600 hover:bg-rose-50 rounded-full font-sans"
                >
                  Cancel
                </Button>
              </div>
            </Card>

            {/* Editing AI Board Help Banner */}
            {currentBoard?.id && currentBoard?.creation_mode === 'ai_assisted' && (
              <div className="mb-4 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-purple-900 mb-1">✨ Editing Your AI-Generated Vision Board</h3>
                    <p className="text-sm text-purple-700 mb-2">
                      This board was created by AI as a starting point. Now make it uniquely yours!
                    </p>
                    <ul className="text-xs text-purple-600 space-y-1 ml-4 list-disc">
                      <li><strong>Click & drag</strong> any image or text to reposition</li>
                      <li><strong>Click an element</strong> to see resize handles (drag corners to resize)</li>
                      <li><strong>Click the trash icon</strong> to delete elements you don't want</li>
                      <li><strong>Add new images</strong> from the Browse/Search options above</li>
                      <li><strong>Upload your own photos</strong> to personalize further</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Vision Board Canvas */}
            <Card className="p-6 bg-white border-2 border-rose-200">
              <div 
                ref={canvasRef}
                className="relative w-full aspect-video border-2 border-dashed border-gray-300 rounded-lg overflow-hidden"
                style={{ backgroundColor: currentBoard?.background_color || '#FEF7ED' }}
                onMouseMove={isResizing ? handleResizeMove : handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                {boardElements.length === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="text-center text-gray-500">
                      <div className="text-4xl mb-2">🎨</div>
                      <p className="font-sans">Start adding images and text to create your vision board</p>
                    </div>
                  </div>
                )}
                
                {boardElements.map((element) => (
                  <div
                    key={element.id}
                    className={`absolute cursor-move rounded-lg border-2 transition-all group ${
                      selectedElement?.id === element.id 
                        ? 'border-rose-500 shadow-lg' 
                        : 'border-transparent hover:border-rose-300'
                    }`}
                    style={{
                      left: `${element.position_x}%`,
                      top: `${element.position_y}%`,
                      width: `${element.width}%`,
                      height: `${element.height}%`,
                      zIndex: element.z_index
                    }}
                    onMouseDown={(e) => handleMouseDown(e, element)}
                    onClick={() => setSelectedElement(element)}
                  >
                    {element.element_type === 'image' ? (
                      <img 
                        src={element.content} 
                        alt="Vision board element"
                        className="w-full h-full object-cover rounded-lg pointer-events-none"
                      />
                    ) : (
                      <div 
                        className="w-full h-full flex items-center justify-center text-center p-2 rounded-lg bg-white/80"
                        style={element.style}
                        contentEditable={isEditingText && selectedElement?.id === element.id}
                        suppressContentEditableWarning
                        onBlur={(e) => {
                          updateElementContent(element.id, e.target.innerText);
                          setIsEditingText(false);
                        }}
                        onDoubleClick={() => setIsEditingText(true)}
                      >
                        {element.content}
                      </div>
                    )}
                    
                    {/* Delete button for selected element */}
                    {selectedElement?.id === element.id && (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteElement(element.id);
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                        
                        {/* Resize handles */}
                        <div
                          onMouseDown={(e) => handleResizeStart(e, element, 'nw')}
                          className="absolute -top-1 -left-1 w-3 h-3 bg-rose-500 border-2 border-white rounded-full cursor-nw-resize opacity-0 group-hover:opacity-100 transition-opacity z-10"
                        />
                        <div
                          onMouseDown={(e) => handleResizeStart(e, element, 'ne')}
                          className="absolute -top-1 -right-1 w-3 h-3 bg-rose-500 border-2 border-white rounded-full cursor-ne-resize opacity-0 group-hover:opacity-100 transition-opacity z-10"
                        />
                        <div
                          onMouseDown={(e) => handleResizeStart(e, element, 'sw')}
                          className="absolute -bottom-1 -left-1 w-3 h-3 bg-rose-500 border-2 border-white rounded-full cursor-sw-resize opacity-0 group-hover:opacity-100 transition-opacity z-10"
                        />
                        <div
                          onMouseDown={(e) => handleResizeStart(e, element, 'se')}
                          className="absolute -bottom-1 -right-1 w-3 h-3 bg-rose-500 border-2 border-white rounded-full cursor-se-resize opacity-0 group-hover:opacity-100 transition-opacity z-10"
                        />
                      </>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Selected Element Controls */}
              {selectedElement && (
                <div className="mt-4 p-4 bg-rose-50 rounded-lg">
                  <p className="text-sm font-semibold text-gray-700 mb-2">
                    Selected: {selectedElement.element_type === 'image' ? 'Image' : 'Text'}
                  </p>
                  <div className="flex gap-2">
                    {selectedElement.element_type === 'text' && (
                      <Button
                        size="sm"
                        onClick={() => setIsEditingText(true)}
                        className="bg-blue-500 hover:bg-blue-600 text-white"
                      >
                        <Edit3 className="w-3 h-3 mr-1" />
                        Edit Text (or double-click)
                      </Button>
                    )}
                    <Button
                      size="sm"
                      onClick={() => deleteElement(selectedElement.id)}
                      className="bg-red-500 hover:bg-red-600 text-white"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </div>
        )}

        {/* AI-Assisted Creation */}
        {viewMode === 'create' && creationMode === 'ai_assisted' && (
          <div className="space-y-6">
            <Card className="p-8 bg-white/90 backdrop-blur-sm border-2 border-rose-200">
              <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4 text-center">
                AI-Assisted Vision Board Creation ✨
              </h2>
              
              <p className="text-gray-600 font-sans text-center mb-8">
                Answer these prompts and let AI create a personalized vision board with powerful affirmations for your manifestation practice
              </p>

              <div className="space-y-6">
                {[
                  {
                    category: "career",
                    prompt: "Career & Success: What does your dream work life look like?",
                    placeholder: "Example: I run my own wellness business, work from a beautiful office, earning $100K+, helping women transform their lives...",
                    icon: "💼"
                  },
                  {
                    category: "relationships", 
                    prompt: "Love & Connections: What kind of relationships do you want? (romantic partner, friendships, family)",
                    placeholder: "Example: A loving partner who supports my dreams, deep friendships with inspiring women, closer bond with family...",
                    icon: "💖"
                  },
                  {
                    category: "lifestyle",
                    prompt: "Dream Lifestyle: Where do you live? How do you spend your days?",
                    placeholder: "Example: Living in a cozy home with a garden, morning yoga, traveling twice a year, peaceful evenings...",
                    icon: "🏡"
                  },
                  {
                    category: "personal_growth",
                    prompt: "Who You're Becoming: What qualities are you developing? How do you feel?",
                    placeholder: "Example: More confident, peaceful, creative, trusting myself, feeling joyful and abundant...",
                    icon: "🌟"
                  }
                ].map((prompt, index) => (
                  <div key={index} className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700 font-serif">
                      <span className="text-lg mr-2">{prompt.icon}</span>
                      {prompt.prompt}
                    </label>
                    <textarea
                      value={aiPromptResponses[prompt.category] || ''}
                      onChange={(e) => setAiPromptResponses({
                        ...aiPromptResponses,
                        [prompt.category]: e.target.value
                      })}
                      placeholder={prompt.placeholder}
                      className="w-full px-4 py-3 border border-rose-200 rounded-lg focus:ring-rose-500 focus:border-rose-500 font-sans text-sm"
                      rows={3}
                    />
                  </div>
                ))}
              </div>

              <div className="mt-8 text-center">
                <Button 
                  onClick={generateAIBoard}
                  disabled={Object.keys(aiPromptResponses).length === 0}
                  className="bg-rose-500 hover:bg-rose-600 text-white rounded-full font-sans text-lg px-8 py-3"
                >
                  ✨ Generate My Vision Board
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Image Library Modal */}
        {showImageLibrary && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-4xl bg-white max-h-[80vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-serif font-bold text-gray-900">Choose Images</h3>
                  <Button 
                    onClick={() => {
                      setShowImageLibrary(false);
                      setSearchQuery('');
                      setUnsplashImages([]);
                    }}
                    variant="outline"
                    className="border-gray-300 text-gray-600"
                  >
                    ✕ Close
                  </Button>
                </div>

                {/* Search Bar */}
                <form onSubmit={handleSearchSubmit} className="mb-6">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search for images (e.g., 'dream home', 'success', 'love')"
                      className="flex-1 px-4 py-2 border border-rose-200 rounded-lg focus:ring-rose-500 focus:border-rose-500"
                    />
                    <Button 
                      type="submit"
                      className="bg-rose-500 hover:bg-rose-600 text-white"
                    >
                      Search
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Try: career goals, luxury lifestyle, peaceful nature, happy couple, fitness inspiration
                  </p>
                </form>

                {/* Upload Option */}
                <div className="mb-6">
                  <label className="block w-full cursor-pointer">
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={(e) => {
                        handleImageUpload(e);
                        setShowImageLibrary(false);
                      }}
                      className="hidden"
                    />
                    <div className="w-full p-4 border-2 border-dashed border-rose-300 rounded-lg hover:border-rose-500 transition-all text-center bg-rose-50">
                      <Upload className="w-8 h-8 text-rose-500 mx-auto mb-2" />
                      <p className="text-sm font-semibold text-gray-700">Upload Your Own Image</p>
                      <p className="text-xs text-gray-500">Click to browse (Max 5MB)</p>
                    </div>
                  </label>
                </div>

                {/* Search Results */}
                {unsplashImages.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-serif font-semibold text-gray-900 mb-3">
                      Search Results for "{searchQuery}"
                    </h4>
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
                      {unsplashImages.map((image, index) => (
                        <button
                          key={index}
                          onClick={() => addImageElement(image.url)}
                          className="aspect-square rounded-lg overflow-hidden border-2 border-gray-200 hover:border-rose-500 transition-all"
                        >
                          <img 
                            src={image.url} 
                            alt={image.alt || 'Search result'}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Stock Image Categories */}
                <div className="space-y-6">
                  <h4 className="font-serif font-semibold text-gray-900">Popular Categories</h4>
                  {Object.entries(Object.keys(curatedStockImages).length > 0 ? curatedStockImages : stockImages).map(([category, images]) => (
                    <div key={category}>
                      <h5 className="font-serif font-medium text-gray-700 mb-3 capitalize text-sm">
                        {category.replace('_', ' ')} Dreams
                      </h5>
                      <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
                        {images.map((imageUrl, index) => (
                          <button
                            key={index}
                            onClick={() => addImageElement(imageUrl)}
                            className="aspect-square rounded-lg overflow-hidden border-2 border-gray-200 hover:border-rose-500 transition-all"
                          >
                            <img 
                              src={imageUrl} 
                              alt={`${category} inspiration`}
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Beautiful Loading Screen for AI Generation */}
        {isGenerating && (
          <div className="fixed inset-0 bg-gradient-to-br from-rose-400/95 via-pink-400/95 to-purple-400/95 backdrop-blur-lg flex items-center justify-center z-50">
            <div className="text-center max-w-md mx-auto px-6">
              {/* Animated Icon */}
              <div className="relative mb-8">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-32 h-32 bg-white/20 rounded-full animate-ping"></div>
                </div>
                <div className="relative flex items-center justify-center">
                  <Sparkles className="w-24 h-24 text-white animate-pulse" />
                </div>
              </div>

              {/* Loading Message */}
              <h2 className="text-3xl font-serif font-bold text-white mb-4 animate-pulse">
                Creating Your Vision Board ✨
              </h2>
              <p className="text-white/90 text-lg mb-6 font-sans">
                AI is crafting beautiful images and empowering affirmations just for you...
              </p>

              {/* Progress Indicator */}
              <div className="w-full bg-white/30 rounded-full h-2 mb-4 overflow-hidden">
                <div className="h-full bg-white rounded-full animate-[shimmer_2s_infinite]" style={{width: '70%'}}></div>
              </div>

              {/* Estimated Time */}
              <p className="text-white/80 text-sm font-sans">
                This usually takes 30-60 seconds
              </p>

              {/* Inspirational Messages */}
              <div className="mt-8 space-y-2">
                <p className="text-white/70 text-sm italic font-sans">
                  "Your vision is the promise of what you shall one day be..."
                </p>
              </div>

              {/* Floating Elements */}
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <Heart className="absolute top-20 left-20 w-6 h-6 text-white/30 animate-float" />
                <Sparkles className="absolute top-40 right-20 w-8 h-8 text-white/30 animate-float-delayed" />
                <Target className="absolute bottom-32 left-32 w-6 h-6 text-white/30 animate-float" />
                <Heart className="absolute bottom-20 right-40 w-8 h-8 text-white/30 animate-float-delayed" />
              </div>
            </div>
          </div>
        )}

        {/* Full-Screen View Modal */}
        {showFullScreen && fullScreenBoard && (
          <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
            <div className="relative w-full max-w-6xl">
              {/* Close Button */}
              <button
                onClick={() => setShowFullScreen(false)}
                className="absolute -top-12 right-0 text-white hover:text-rose-300 transition-colors"
              >
                <span className="text-3xl">✕</span>
              </button>

              {/* Board Title */}
              <div className="text-center mb-4">
                <h2 className="text-2xl font-serif font-bold text-white">{fullScreenBoard.title}</h2>
                <p className="text-white/70 text-sm">{fullScreenBoard.description}</p>
              </div>

              {/* Vision Board Display */}
              <div 
                className="relative w-full aspect-video rounded-lg shadow-2xl overflow-hidden"
                style={{ backgroundColor: fullScreenBoard.background_color || '#FEF7ED' }}
              >
                {fullScreenBoard.elements && fullScreenBoard.elements.length > 0 ? (
                  fullScreenBoard.elements.map((element, idx) => (
                    <div
                      key={idx}
                      className="absolute"
                      style={{
                        left: `${element.position_x}%`,
                        top: `${element.position_y}%`,
                        width: `${element.width}%`,
                        height: `${element.height}%`,
                        zIndex: element.z_index
                      }}
                    >
                      {element.element_type === 'image' ? (
                        <img 
                          src={element.content} 
                          alt=""
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : (
                        <div 
                          className="w-full h-full flex items-center justify-center text-center p-2 rounded-lg bg-white/80"
                          style={element.style}
                        >
                          {element.content}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-6xl">🖼️</span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-center gap-4 mt-6">
                <Button
                  onClick={() => downloadBoard(fullScreenBoard)}
                  className="bg-green-500 hover:bg-green-600 text-white rounded-full font-sans px-6"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
                <Button
                  onClick={() => {
                    setShowFullScreen(false);
                    editBoard(fullScreenBoard);
                  }}
                  className="bg-blue-500 hover:bg-blue-600 text-white rounded-full font-sans px-6"
                >
                  <Edit3 className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VisionBoard;